// The only place this module talks to C2 (canonical spec §"Files to change").
// Every response is typed as the S8 envelope, so `meta` is never optional — a
// component that could render without freshness metadata does not typecheck.

export type FreshnessMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
};

export type Enveloped<T> = { data: T; meta: FreshnessMeta };

export type Scope = {
  region: string;
  store: string;
  channel: string;
  locale: string;
};

export type Fact = {
  fact_id: string;
  fact_key: string;
  fact_class: number;
  scope: Scope;
  scope_id: string;
  formula_version_id: string | null;
  /**
   * The product a value describes, landed by cvce PR 360. A nutrition number with
   * no product is unreadable — "899 kcal" is not a fact, "899 kcal per 100 g of
   * MCT Oil C8, formula v7" is — and `formula_version_id` alone is a UUID no
   * human can resolve.
   *
   * All four are `null` TOGETHER when the fact is not formula-scoped. That is
   * the company-wide family (shipping, policy), and the UI contract reads it as
   * "Every product" — never as a name it failed to look up, and never a blank.
   */
  product_key: string | null;
  product_name: string | null;
  formula_version_label: string | null;
  formula_effective_from: string | null;
  value: Record<string, unknown>;
  unit: string | null;
  authority_grade: string;
  status: "draft" | "approved" | "superseded" | "rejected";
  version: number;
  approved_by: string | null;
  approved_at: string | null;
  /**
   * The fact's short human name (cvce `FactOut.name`, KET-6326). Optional and
   * nullable: cvce migration p0011 runs only after the cvce deploy, so until it
   * has, the field is absent or null and the title falls back (`factTitle`).
   */
  name?: string | null;
};

export type FactKey = {
  fact_key: string;
  domain: string;
  attribute: string;
  fact_class: number;
  value_type: string;
  unit: string | null;
  description: string;
  scoped_by_formula: boolean;
  /** The key's short human name, beside the unchanged `description` (see `Fact.name`). */
  name?: string | null;
  /** Not sent today either; searchable when present. */
  definition?: string | null;
};

export type ApprovalEvent = {
  event_id: string;
  action: "approve" | "reject" | "supersede";
  actor: string;
  self_approved: boolean;
  prior_value: Record<string, unknown> | null;
  source_locator: string | null;
  created_at: string;
};

/**
 * A cross-fact audit row: an approval event plus the fact it belongs to.
 *
 * The per-fact read can leave the fact implicit — you already picked it. A feed
 * cannot: `fact_key` is what you scan, `fact_id` is what you click through to.
 */
export type AuditEvent = ApprovalEvent & {
  fact_id: string;
  fact_key: string;
};

/**
 * How far an approval reaches, from `GET /v1/facts/{id}/impact` (cvce PR 360).
 *
 * `surfaces` counts published LOCATORS, not renders: one render pushed to three
 * locators is three places a wrong value is visible. A fact that reaches nothing
 * returns `0` — a legitimate answer, and a different claim from "we could not
 * find out", which is a failed request and renders as its own line.
 */
export type FactImpact = {
  fact_id: string;
  surfaces: number;
  markets: string[];
};

export type FactPage = {
  items: Fact[];
  total: number;
  limit: number;
  offset: number;
};

export type AuditPage = {
  items: AuditEvent[];
  total: number;
  limit: number;
  offset: number;
};

/**
 * One policy question the CVCE front door filed for the Protex owner to decide
 * (cvce `DecisionRequestOut`, P1/P3). The raw customer message, name and email
 * are never part of it by contract (parent spec section 5, PII rule).
 */
export type DecisionRequest = {
  request_id: string;
  kind: string;
  core_need_id: string;
  conversation_id: string | null;
  question_text: string;
  category: string;
  raised_by: string;
  status: "open" | "decided" | "withdrawn";
  cortex_question_id: string | null;
  /**
   * The decision as the server recorded it. An OPEN request holds one when it was
   * deferred (`deferred: true`); a decided one holds the published decision.
   */
  decision: Record<string, unknown> | null;
  decided_by: string | null;
  decided_at: string | null;
  fact_id: string | null;
  created_at: string;
  updated_at: string;
};

export type DecisionRequestPage = {
  items: DecisionRequest[];
  total: number;
  limit: number;
  offset: number;
};

export type RampBody = {
  start_ml: number;
  target_ml: number;
  step_ml: number;
  step_interval_days: number;
  condition: string;
};

/** cvce `DecideIn`. `topic` belongs to coverage, `formula_version_id` and `ramp` to a ramp. */
export type DecideIn = {
  decision_type: "support_coverage" | "usage_ramp";
  outcome: "cover" | "do_not_cover" | "defer";
  rationale: string;
  scope: Scope;
  topic?: string;
  cortex_question_id?: string;
  formula_version_id?: string;
  ramp?: RampBody;
};

// Same-origin via the Pages Function proxy (functions/protex/[[path]].ts) —
// routing decision (Damien, 2026-09-02): no protex hostname, everything goes
// through os.ketosource.co.
const BASE = import.meta.env.VITE_PROTEX_API_BASE ?? "/protex/v1";

async function get<T>(path: string): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
  });
  if (!response.ok) {
    throw new ProtexError(response.status, await safeBody(response));
  }
  return (await response.json()) as Enveloped<T>;
}

async function post<T>(path: string, body: unknown): Promise<Enveloped<T>> {
  const response = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ProtexError(response.status, await safeBody(response));
  }
  return (await response.json()) as Enveloped<T>;
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export class ProtexError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`PROTEX API ${status}`);
  }
}

export const listFactKeys = () => get<FactKey[]>("/fact-keys");

export const listFacts = (params: {
  fact_key?: string;
  status?: string;
  /** A product key. Narrows the list to facts about that one product (cvce PR 360). */
  product?: string;
  limit: number;
  offset: number;
}) => {
  const query = new URLSearchParams();
  if (params.fact_key) query.set("fact_key", params.fact_key);
  if (params.status) query.set("status", params.status);
  if (params.product) query.set("product", params.product);
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  return get<FactPage>(`/facts?${query.toString()}`);
};

/**
 * The ceiling on how far `listEveryFact` will read. 10 x 200 = 2000 records —
 * far past any product's real catalogue, and a bound rather than a promise:
 * beyond it the caller is handed a page that does not hold every record and
 * `holdsEveryRecord` says so, which is the honest answer rather than an
 * unbounded read against a server that could be handing back anything.
 */
const MAX_PAGES = 10;

/**
 * Every record matching a query, not just its first page.
 *
 * The Facts tab's two hardest claims — which registry keys have NO fact, and
 * how many facts need attention — are both statements about a COMPLETE set,
 * and a single 200-record page can only make them about a product small enough
 * to fit. A product past that page had rows that could not be reached at all
 * (nothing on the tab ever asked for offset 200) and a counts strip pinned
 * permanently on "Counts unavailable", because `holdsEveryRecord` compares
 * what we hold against the SERVER's total and a capped page never matches it.
 *
 * Paging here rather than in the component is deliberate: the completeness of
 * the read is a property of the read, and every consumer downstream already
 * decides what it may claim from `total` vs `items.length`. Those two are
 * returned unchanged, so a read that hits the ceiling still reports itself as
 * incomplete and every "we could not read the full list" path stays live.
 */
export async function listEveryFact(
  params: { fact_key?: string; status?: string; product?: string },
  { limit = 200, maxPages = MAX_PAGES }: { limit?: number; maxPages?: number } = {},
): Promise<Enveloped<FactPage>> {
  const first = await listFacts({ ...params, limit, offset: 0 });
  const items = [...first.data.items];
  for (let page = 1; page < maxPages && items.length < first.data.total; page += 1) {
    const next = await listFacts({ ...params, limit, offset: items.length });
    // A page that answers with nothing cannot advance the offset, so believing
    // `total` over it would spin. The short read is the end of the list
    // whatever the count said.
    if (next.data.items.length === 0) break;
    items.push(...next.data.items);
  }
  return { ...first, data: { ...first.data, items } };
}

/**
 * The Inbox queue (cvce `GET /v1/requests`), newest first, server-paginated.
 *
 * `fresh` asks the server for `Cache-Control: no-store`. Without it the answer is
 * `private, max-age=30`, so the list read straight after a decision could be the
 * browser's 30-second-old copy and show the row still waiting.
 */
export const listRequests = (params: {
  status: "open" | "decided" | "all";
  limit: number;
  offset: number;
  fresh?: boolean;
}) => {
  const query = new URLSearchParams();
  query.set("status", params.status);
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  if (params.fresh) query.set("fresh", "1");
  return get<DecisionRequestPage>(`/requests?${query.toString()}`);
};

/** Record one decision (cvce `POST /v1/requests/{id}/decide`). One transaction, server-side. */
export const decideRequest = (requestId: string, body: DecideIn) =>
  post<DecisionRequest>(`/requests/${requestId}/decide`, body);

export const getFact = (factId: string) => get<Fact>(`/facts/${factId}`);

export const getFactAudit = (factId: string) => get<ApprovalEvent[]>(`/facts/${factId}/audit`);

/**
 * The blast radius shown before Approve is pressed (C3 interaction rule 3).
 *
 * KET-3853 closed having corrected 1 published panel of 13 because nothing ever
 * showed how many surfaces a value reached. A failure here does NOT block the
 * approval — it renders the "we could not confirm" line instead, because a
 * fabricated count and a silent zero are both worse than a stated unknown.
 */
export const getFactImpact = (factId: string) => get<FactImpact>(`/facts/${factId}/impact`);

/**
 * The cross-fact audit feed (C2 `GET /v1/audit`). Server-paginated: the portal
 * is not a backend and never holds a full collection, so `limit`/`offset` are
 * required rather than optional.
 */
export const listAudit = (params: {
  fact_key?: string;
  actor?: string;
  action?: string;
  limit: number;
  offset: number;
}) => {
  const query = new URLSearchParams();
  if (params.fact_key) query.set("fact_key", params.fact_key);
  if (params.actor) query.set("actor", params.actor);
  if (params.action) query.set("action", params.action);
  query.set("limit", String(params.limit));
  query.set("offset", String(params.offset));
  return get<AuditPage>(`/audit?${query.toString()}`);
};

/**
 * Every audit event about ONE product, newest first (KAL-410).
 *
 * `GET /v1/audit` narrows by `fact_key` only — it has no `product` parameter,
 * and FastAPI ignores an unknown one, so asking it for a product would return
 * the whole trail and look like a filter that works. The product's events are
 * read the way the store links them instead: the product's facts
 * (`GET /v1/facts?product=`, which joins through the formula version), then
 * each fact's own trail (`GET /v1/facts/{id}/audit`), merged into one feed.
 *
 * Bounded by one product's catalogue, which is tens of facts. `complete` is
 * false when that catalogue itself was too long to read in full, so the tab
 * can say some events may be missing rather than imply it holds them all.
 * The permanent fix is a `product` filter on C2's `/v1/audit`.
 */
export async function listProductAudit(
  product: string,
): Promise<{ items: AuditEvent[]; complete: boolean }> {
  const facts = await listEveryFact({ product });
  const ids = facts.data.items.map((fact) => ({ id: fact.fact_id, key: fact.fact_key }));
  const trails: AuditEvent[][] = [];
  // A few at a time: a product with forty facts must not open forty sockets.
  for (let start = 0; start < ids.length; start += AUDIT_FANOUT) {
    const batch = ids.slice(start, start + AUDIT_FANOUT);
    const pages = await Promise.all(batch.map(({ id }) => getFactAudit(id)));
    pages.forEach((page, index) => {
      trails.push(
        page.data.map((event) => ({
          ...event,
          fact_id: batch[index].id,
          fact_key: batch[index].key,
        })),
      );
    });
  }
  return {
    items: mergeAuditTrails(trails),
    complete: facts.data.items.length >= facts.data.total,
  };
}

const AUDIT_FANOUT = 6;

/**
 * Per-fact trails into one feed, in the cross-fact feed's own order: newest
 * first, ties broken by event id descending, as `GET /v1/audit` sorts them.
 */
export function mergeAuditTrails(trails: AuditEvent[][]): AuditEvent[] {
  return trails.flat().sort((a, b) => {
    const at = Date.parse(b.created_at) - Date.parse(a.created_at);
    if (at !== 0) return at;
    return a.event_id < b.event_id ? 1 : a.event_id > b.event_id ? -1 : 0;
  });
}

export const draftFact = (payload: {
  fact_key: string;
  scope: Scope;
  formula_version_id: string | null;
  value: Record<string, unknown>;
  unit: string | null;
  authority_grade: string;
}) => post<Fact>("/facts", payload);

export const approveFact = (factId: string, sourceLocator: string) =>
  post<Fact>(`/facts/${factId}/approve`, { source_locator: sourceLocator });

/**
 * Build the replacement value in the SAME shape as the value being corrected.
 *
 * A fact's value is a typed envelope — `{ n: 15 }` for a number, `{ s: "GBP" }`
 * for a string — and the shape is part of what C2 stores. Coercing every edit
 * to `{ n: Number(input) }` turns a string fact's correction into `NaN`, which
 * `JSON.stringify` sends as `null`: a silently corrupt draft that reads as a
 * real value downstream. So the shape comes from the fact being edited, and a
 * non-numeric entry against a numeric fact is REFUSED here rather than sent.
 *
 * `valueType` is the fact key's declared `value_type` when known; the current
 * value's own shape is the fallback, because a fact always has one and a fact
 * key lookup may not have resolved yet.
 */
export function draftValueFor(
  current: Record<string, unknown>,
  input: string,
  valueType?: string,
): { value: Record<string, unknown> } | { error: string } {
  // A COMPOUND value never comes through here. The `!numeric` branch below
  // returns `{ s: input }` for anything that is not a number, so an object fact
  // — `nutrition.active_ingredients`, stored as `{ items: [...] }` — had its
  // correction SAVED as a string under a key the store holds as an object. It
  // did not fail and nothing on screen said so; every reader downstream then
  // misread the shape. The pane now routes those to the structured editor
  // (`buildValue`, `ValueKind`), and this is the backstop for any caller that
  // does not: refuse, rather than coerce into a corrupt shape.
  if (holdsStructuredValue(current, valueType)) {
    return {
      error: "This fact holds a structured value. It cannot be corrected as a single entry.",
    };
  }
  const numeric = valueType ? /^(number|numeric|int|integer|float|decimal)$/i.test(valueType) : "n" in current;
  if (!numeric) return { value: { s: input } };
  const parsed = Number(input.trim());
  if (input.trim() === "" || !Number.isFinite(parsed)) {
    return { error: "That is not a number. This fact holds a number — enter digits only." };
  }
  return { value: { n: parsed } };
}

/**
 * Whether a DECLARED type names a compound shape.
 *
 * The DECLARED half of `holdsStructuredValue`, split out because the registry's
 * own answer is the one that holds even for a key with no record yet. While the
 * pane tested only the literal `"object"`, a `json`- or `struct`-typed key
 * rendered a text box that the backstop then refused on every blur — an
 * editable field whose only possible answer was "this cannot be corrected as a
 * single entry". Two spellings of one predicate is how that happened.
 */
export function isStructuredType(valueType?: string): boolean {
  return valueType !== undefined && /^(object|json|struct)$/i.test(valueType);
}

/**
 * Whether a value is an OBJECT envelope rather than a scalar one.
 *
 * The registry's declared type wins when there is one — it is the store's own
 * answer, and it is right even for a key with no record yet. Otherwise the
 * envelope's own shape decides: every scalar carries `n` or `s`, so a value
 * with neither is compound whatever else it holds.
 *
 * That second branch only holds while `current` is a value the store actually
 * returned. A FABRICATED envelope defeats it — see `buildValue`, which used to
 * invent `{ s: "" }` for a row with no record and thereby handed this function
 * the `"s"` key it reads as proof of a scalar.
 *
 * EXPORTED because the pane must choose its control from the SAME answer this
 * backstop will give, on the same envelope. While the two asked the question
 * separately they disagreed, and the disagreement was not theoretical: an
 * untyped key with no record read as `"string"` up there — an editable text box
 * — and as compound down here, so every save came back "This fact holds a
 * structured value." A field whose only possible answer is that it cannot be
 * edited is the same defect as the corruption it replaced, pointing the other
 * way. One function, asked once, is the only thing that makes that unreachable.
 */
export function holdsStructuredValue(
  current: Record<string, unknown>,
  valueType?: string,
): boolean {
  if (valueType) return isStructuredType(valueType);
  return !("n" in current) && !("s" in current);
}

/**
 * One message per C2 failure (canonical spec HARD 7): 403 names who to ask,
 * 404 says the fact is gone, 409 says someone approved a different draft
 * first, 422 names the unregistered key, 503 says nothing is shown and why.
 * A non-`ProtexError` (a network failure, an unconfigured API base) degrades
 * to the SAME "nothing is shown" statement as a 503 — never to a blank or an
 * empty list, which would assert the false claim "there is nothing".
 *
 * `areaName` is REQUIRED, and comes from the registry via the shell (I-5, AGENTS
 * boundary 8). Hardcoding "Core Tools" here would leave the 403 naming the wrong
 * area the day PROTEX is renamed or reassigned, so the name is not a literal a
 * module is allowed to hold.
 */
export function messageFor(error: unknown, areaName: string): string {
  if (!(error instanceof ProtexError)) {
    return "Permission could not be confirmed, so nothing is shown. Try again shortly.";
  }
  switch (error.status) {
    case 403:
      return `You do not have permission to change this. Ask the ${areaName} area owner.`;
    case 404:
      return "This fact no longer exists.";
    case 409:
      return "Someone approved a different draft first. Reload to see the current value.";
    case 422:
      return "That fact key is not registered. Pick one from the list.";
    case 503:
      return "Permission could not be confirmed, so nothing is shown. Try again shortly.";
    default:
      return "Something went wrong. Try again.";
  }
}
